import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import {
  Alert,
  Box,
  Button,
  Heading,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useMemo } from 'react';

import { useAppSettings } from '#providers/app-settings-provider';

import {
  buildGoogleMapsEmbedUrl,
  describeMarkdownMap,
  getMarkdownMapValidationMessage,
  mapBlockDataToEditValues,
  markdownMapEditValuesToBlockData,
  normalizeMapBlockData,
  parseMarkdownMapConfig,
  serializeMarkdownMapBlock,
  serializeMarkdownMapInnerConfig,
  stringifyMarkdownMapConfig,
  type MapBlockData,
  type MarkdownMapEditValues,
  type MarkdownMapEditRequest,
  NP_MAP_TAG,
} from './markdown-map';

interface MarkdownMapNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownMapNodeAttrs {
  data: MapBlockData;
  rawConfig: string;
  parseError: string | null;
}

function normalizeMapAttrs(attrs: unknown): MarkdownMapNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};

  return {
    data: normalizeMapBlockData(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MapNodePreview({
  attrs,
  canEdit,
  onEdit,
}: {
  attrs: MarkdownMapNodeAttrs;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { googleMapsEmbedApiKey } = useAppSettings();
  const validationMessage = getMarkdownMapValidationMessage(attrs.data);
  const previewUrl = useMemo(
    () =>
      googleMapsEmbedApiKey
        ? buildGoogleMapsEmbedUrl(attrs.data, googleMapsEmbedApiKey)
        : null,
    [attrs.data, googleMapsEmbedApiKey],
  );

  return (
    <VStack align="stretch" gap={3}>
      <HStack justify="space-between" align="start" gap={3} flexWrap="wrap">
        <Box>
          <Heading as="h2" size="sm">
            Map
          </Heading>
          <Text fontSize="sm" color="fg.muted">
            {describeMarkdownMap(attrs.data)}
          </Text>
        </Box>
        <Button
          size="xs"
          variant="outline"
          onClick={onEdit}
          disabled={!canEdit}
        >
          Edit map
        </Button>
      </HStack>

      {previewUrl ? (
        <Box className="markdown-map-node__frame">
          <iframe
            title={describeMarkdownMap(attrs.data)}
            src={previewUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            style={{
              width: '100%',
              minHeight: `${attrs.data.height}px`,
              border: 0,
              borderRadius: '12px',
            }}
          />
        </Box>
      ) : (
        <Box className="markdown-map-node__placeholder">
          <Text fontSize="sm" color="fg.muted">
            {googleMapsEmbedApiKey
              ? 'Complete the map fields to preview the embed.'
              : 'Google Maps key is not configured for this environment.'}
          </Text>
        </Box>
      )}

      {attrs.parseError ? (
        <Alert.Root status="warning" borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Invalid map config</Alert.Title>
            <Alert.Description>{attrs.parseError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {!attrs.parseError && validationMessage ? (
        <Alert.Root status="warning" borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Incomplete map config</Alert.Title>
            <Alert.Description>{validationMessage}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
    </VStack>
  );
}

function MarkdownMapNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownMapNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeMapAttrs(node.attrs);

  const handleEdit = () => {
    context.onRequestMapEdit?.({
      mode: 'edit',
      initialValues: mapBlockDataToEditValues(attrs.data),
      onCommit: async (next: MarkdownMapEditValues) => {
        const normalized = markdownMapEditValuesToBlockData(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyMarkdownMapConfig(normalized),
          parseError: null,
        });
      },
    } satisfies MarkdownMapEditRequest);
  };

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-map-node markdown-map-node--selected'
          : 'markdown-map-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <MapNodePreview
        attrs={attrs}
        canEdit={Boolean(context.onRequestMapEdit)}
        onEdit={handleEdit}
      />
    </NodeViewWrapper>
  );
}

export const MarkdownMapNode = Node.create<MarkdownMapNodeOptions>({
  name: 'npMap',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeMapBlockData({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_MAP_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownMapConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeMapAttrs(HTMLAttributes);
    return [NP_MAP_TAG, serializeMarkdownMapInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeMapAttrs(node?.attrs);
    return serializeMarkdownMapBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownMapNodeView);
  },
});
